import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MedalMilitary, ShieldCheck, Lightning } from "@phosphor-icons/react";
import { useLang } from "../context/LangContext";

const HERO_GALLERY = [
  { src: "https://images.unsplash.com/photo-1493225458063-7b6849da42e3?w=900&q=70", caption: "Line Array" },
  { src: "https://images.unsplash.com/photo-1571266028243-d220c9c3b31f?w=900&q=70", caption: "Point Source" },
  { src: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=900&q=70", caption: "Subwoofers" },
  { src: "https://images.unsplash.com/photo-1487349384428-12b47aca925e?w=900&q=70", caption: "Install Systems" },
];

export default function AmateAudio() {
  const { lang } = useLang();

  const T = {
    eyebrow: { en: "Authorized Partner", gr: "Επίσημος Συνεργάτης" },
    title: { en: "Amate Audio.", gr: "Amate Audio." },
    title2: { en: "Made in Spain. Built for the night.", gr: "Κατασκευασμένο στην Ισπανία. Φτιαγμένο για τη νύχτα." },
    intro: {
      en: "MP Sound is the authorized dealer and installation partner for Amate Audio in Cyprus. For over 40 years, Amate Audio has engineered some of the most respected loudspeaker systems in Europe — from intimate clubs in Barcelona to arenas, theatres and houses of worship worldwide. We bring that engineering to every system we build.",
      gr: "Η MP Sound είναι ο επίσημος αντιπρόσωπος και συνεργάτης εγκαταστάσεων της Amate Audio στην Κύπρο. Για πάνω από 40 χρόνια, η Amate Audio σχεδιάζει ηχοσυστήματα παγκοσμίου φήμης — από clubs στη Βαρκελώνη μέχρι αρένες, θέατρα και χώρους λατρείας σε όλο τον κόσμο.",
    },
    why_title: { en: "Why Amate Audio.", gr: "Γιατί Amate Audio." },
    pillars: [
      {
        icon: MedalMilitary,
        title: { en: "40+ Years of R&D", gr: "40+ Χρόνια Έρευνας" },
        body: { en: "Spanish family-owned manufacturer with proprietary driver design, transducer engineering and DSP — all in-house.", gr: "Ισπανικός οικογενειακός κατασκευαστής με αποκλειστικό σχεδιασμό drivers και DSP — όλα εσωτερικά." },
      },
      {
        icon: ShieldCheck,
        title: { en: "Certified by Touring Pros", gr: "Πιστοποιημένο από Επαγγελματίες" },
        body: { en: "Used on world tours, EDM mainstages, broadcast studios and high-end installs. Built like a tank, tuned like a violin.", gr: "Χρησιμοποιείται σε παγκόσμιες περιοδείες, EDM mainstages, στούντιο και εγκαταστάσεις υψηλού επιπέδου." },
      },
      {
        icon: Lightning,
        title: { en: "Designed for Cyprus venues", gr: "Σχεδιασμένο για χώρους στην Κύπρο" },
        body: { en: "From beach clubs to mountain venues, Amate's coaxial point-source and Nítid line arrays handle our temperature, salt-air and humidity without compromise.", gr: "Από beach clubs μέχρι ορεινούς χώρους, οι σειρές Nítid χειρίζονται θερμοκρασία, αλάτι και υγρασία χωρίς συμβιβασμούς." },
      },
    ],
    cta_title: { en: "See Amate Audio in our catalogue.", gr: "Δείτε τα προϊόντα Amate Audio στον κατάλογο." },
    cta_sub: { en: "Browse the lineup, request a quote, or talk to us about a system design for your venue.", gr: "Δείτε όλα τα προϊόντα, ζητήστε προσφορά, ή μιλήστε μαζί μας για το σύστημα του χώρου σας." },
    cta_btn: { en: "View Amate Audio Products", gr: "Δείτε Προϊόντα Amate Audio" },
    cta_btn2: { en: "Request Consultation", gr: "Ραντεβού Συμβουλευτικής" },
    official_btn: { en: "Visit amateaudio.com", gr: "Επισκεφθείτε amateaudio.com" },
    gallery_eyebrow: { en: "From the catalogue", gr: "Από τον κατάλογο" },
  };

  return (
    <div className="bg-[#08080a] text-white" data-testid="amate-audio-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(ellipse at top right, #ccff00 0%, transparent 60%)" }} />
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="label-tag slash mb-4">{T.eyebrow[lang]}</div>
            <h1 className="display-font text-5xl sm:text-7xl font-black uppercase leading-[0.88] tracking-tighter">
              {T.title[lang].split(" ")[0]} <span className="neon-text">{T.title[lang].split(" ").slice(1).join(" ")}</span>
            </h1>
            <p className="display-font text-xl sm:text-2xl text-gray-300 mt-4 max-w-xl">{T.title2[lang]}</p>
            <p className="text-base text-gray-400 mt-6 max-w-2xl leading-relaxed">{T.intro[lang]}</p>

            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/products?brand=Amate" className="btn-primary" data-testid="amate-view-products">
                {T.cta_btn[lang]} <ArrowUpRight size={18} weight="bold" />
              </Link>
              <a href="https://www.amateaudio.com" target="_blank" rel="noreferrer"
                className="btn-secondary" data-testid="amate-visit-official">
                {T.official_btn[lang]} <ArrowUpRight size={18} weight="bold" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 flex items-center justify-center"
          >
            <div className="relative w-full max-w-md aspect-square flex items-center justify-center
                            border border-white/10 bg-gradient-to-br from-[#101015] to-[#070708] p-8">
              <img src="/brands/amate-audio-logo.png" alt="Amate Audio"
                className="w-full h-full object-contain drop-shadow-[0_0_40px_rgba(204,255,0,0.15)]" />
              <div className="absolute -top-3 -left-3 mono-font text-[10px] tracking-[0.25em] uppercase
                              bg-[#ccff00] text-black px-3 py-1.5 font-bold">
                Authorized · Cyprus
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY AMATE */}
      <section className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
          <h2 className="display-font text-4xl sm:text-5xl font-black uppercase mb-12 leading-[0.9]">
            {T.why_title[lang]}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {T.pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="card-dark p-7" data-testid={`amate-pillar-${i}`}
                >
                  <Icon size={32} weight="duotone" className="text-[#ccff00] mb-4" />
                  <h3 className="display-font text-xl font-bold uppercase mb-3">{p.title[lang]}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{p.body[lang]}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
          <div className="label-tag mb-4 slash">{T.gallery_eyebrow[lang]}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {HERO_GALLERY.map((g, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative aspect-square overflow-hidden bg-[#101015] group"
                data-testid={`amate-gallery-${i}`}
              >
                <img src={g.src} alt={g.caption}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500 group-hover:scale-105" />
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                  <span className="mono-font text-[10px] uppercase tracking-[0.25em] text-white">{g.caption}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 text-center">
          <h2 className="display-font text-4xl sm:text-6xl font-black uppercase leading-[0.9] mb-4">
            {T.cta_title[lang].split(" ").slice(0, -1).join(" ")} <span className="neon-text">{T.cta_title[lang].split(" ").slice(-1)[0]}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">{T.cta_sub[lang]}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/products?brand=Amate" className="btn-primary" data-testid="amate-cta-products">
              {T.cta_btn[lang]} <ArrowUpRight size={18} weight="bold" />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="amate-cta-contact">
              {T.cta_btn2[lang]} <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
